import { buildApiClientFromNextJsContext } from '@/api/apiClient';
import { buildUrlFromSearch, Search } from '@/api/search';
import { ApiClientOptions } from '@/types/apiclient';
import { Group, JoinGroupRequest } from '@/types/core/Group';
import { CreateGroupDTO } from '@/types/dto/CreateGroupDTO';
import { SearchResult } from '@/types/search/searchresult';
import { EditGroupDTO } from '@/types/dto/EditGroupDTO';
import { User } from '@/types/core/User';

export async function searchGroups(
  search: Search,
  options?: ApiClientOptions
): Promise<SearchResult<Group>> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);

  const url = buildUrlFromSearch(`/api/groups`, search);

  const response = await apiClient(url, {
    method: 'GET',
  });
  return response.json();
}

export async function getUserGroup(
  userId: string,
  options?: ApiClientOptions
): Promise<SearchResult<Group>> {
  return searchGroups(
    {
      fields: {
        group: 'name,private,slug',
        country: 'code',
      },
      filters: {
        member: userId,
      },
      relationIncludes: ['country'],
      orders: ['name'],
      pagination: {
        countTotalResults: true,
        itemsPerPage: 100,
      },
    },
    options
  );
}

export async function getThematicsGroup(
  options?: ApiClientOptions
): Promise<SearchResult<Group>> {
  return searchGroups(
    {
      fields: {
        group: 'name,private,slug',
        country: 'code',
      },
      filters: {
        country: 'null',
        haveFaq: 'false',
        notType: 'expertise',
      },
      relationIncludes: ['country'],
      orders: ['name'],
      pagination: {
        countTotalResults: true,
        itemsPerPage: 100,
      },
    },
    options
  );
}

export async function getCountryGroups(
  countryId: string,
  options?: ApiClientOptions
): Promise<SearchResult<Group>> {
  return searchGroups(
    {
      fields: {
        group: 'name,private,slug',
        country: 'code',
      },
      filters: {
        country: countryId,
      },
      relationIncludes: ['country'],
      orders: ['name'],
      pagination: {
        countTotalResults: true,
        itemsPerPage: 100,
      },
    },
    options
  );
}

export async function getGroup(
  groupId: string,
  userId?: string,
  options?: ApiClientOptions
): Promise<Group | null> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);

  const search: Search = {
    fields: {
      group:
        'name,slug,goal,target,subjects,trainingGoals,disciplines,countMembers,isMember,hasJoinRequest,private,haveFaq,type',
      country: 'name,primaryColor,code',
      file: 'originalFilename,filename',
      user: 'displayName,slug,jobs,disciplines,roles',
      media: 'title,description,mediaType,alt',
      document: 'createdAt',
      publication: 'title',
    },
    relationIncludes: [
      'country',
      'admin',
      'admin.country',
      'admin.avatar',
      'discussion',
      'createdBy',
      'documents',
      'documents.media',
      'documents.media.file',
      'documents.author',
      'documents.discussion',
    ],
  };

  // If we pass a userId, we want to know if the creator of the group is a colleague of the user
  if (userId) {
    search.fields.user += `,colleagueState:${userId}`;
    search.relationIncludes.push('createdBy.country');
  }
  const url = buildUrlFromSearch(`/api/groups/${groupId}`, search);

  const response = await apiClient(url, {
    method: 'GET',
  });
  if (response.status !== 200) {
    return null;
  }
  return response.json();
}

export async function createGroup(
  group: CreateGroupDTO,
  options?: ApiClientOptions
) {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups`, {
    method: 'POST',
    body: JSON.stringify(group),
  });
}

export async function editGroup(
  groupId: string,
  group: EditGroupDTO,
  options?: ApiClientOptions
) {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}`, {
    method: 'PUT',
    body: JSON.stringify(group),
  });
}

export async function joinPrivateGroup(
  groupId: string,
  content: JoinGroupDTO,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}/join-request`, {
    method: 'POST',
    body: JSON.stringify(content),
  });
}

export async function joinPublicGroup(
  groupId: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}/join`, {
    method: 'PATCH',
  });
}

export async function leaveGroup(
  groupId: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}/leave`, {
    method: 'PATCH',
  });
}

export async function getGroupMembers(
  groupId: string,
  userId: string,
  search: Search,
  options?: ApiClientOptions
): Promise<SearchResult<User>> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);

  const baseSearch: Search = {
    filters: {
      group: groupId,
    },
    orders: ['firstnameAndLastname'],
    fields: {
      user: `displayName,slug,jobs,disciplines,colleagueState:${userId}`,
      file: 'originalFilename,filename',
      country: 'name,code',
    },
    relationIncludes: ['country', 'avatar'],
  };

  if (search.filters) {
    baseSearch.filters = {
      ...baseSearch.filters,
      ...search.filters,
    };
  }

  if (search.pagination) {
    baseSearch.pagination = search.pagination;
  }

  const response = await apiClient(
    buildUrlFromSearch('/api/users', baseSearch),
    options
  );

  return response.json();
}

export async function getGroupFaq(
  groupId: string,
  options?: ApiClientOptions
): Promise<Group> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  const params: Search = {
    fields: {
      faqQuestion: 'question,rank,createdAt',
      faqAnswer: 'type,rank,richTextContent',
      media: 'title,description,mediaType',
      videoMedia: 'url,imagePreview',
      soundMedia: 'url',
      file: 'filename',
      linkMedia: 'url',
    },
    relationIncludes: [
      'faqQuestions',
      'faqQuestions.answers',
      'faqQuestions.answers.medias',
      'faqQuestions.answers.medias.file',
    ],
  };
  const url = buildUrlFromSearch(`/api/groups/${groupId}`, params);
  const response = await apiClient(url, {
    method: 'GET',
  });
  return response.json();
}

export async function changeGroupAdmin(
  groupId: string,
  data: any,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}/change-admin`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getPendingJoinGroupRequest(
  groupId: string,
  options?: ApiClientOptions
): Promise<SearchResult<JoinGroupRequest>> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);

  const parameters: Search = {
    fields: {
      joinGroupRequest: 'requestMessage,createdAt',
      user: 'displayName,slug,roles,jobs',
      country: 'code,name',
    },
    relationIncludes: ['user', 'user.country'],
  };

  const response = await apiClient(
    buildUrlFromSearch(
      `/api/groups/${groupId}/pendingJoinGroupRequest`,
      parameters
    ),
    options
  );

  return response.json();
}

export async function acceptGroupRequest(
  pendingJoinGroupRequestId: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(
    `/api/groups/join-request/${pendingJoinGroupRequestId}/accept`,
    {
      method: 'PATCH',
    }
  );
}

export async function refuseJoinGroupRequest(
  pendingJoinGroupRequestId: string,
  message?: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(
    `/api/groups/join-request/${pendingJoinGroupRequestId}/refuse`,
    {
      method: 'PATCH',
      body: JSON.stringify({ message }),
    }
  );
}

export async function deleteGroup(
  groupId: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}`, {
    method: 'DELETE',
  });
}

export async function removeGroupMember(
  groupId: string,
  userId: string,
  message: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}/members/${userId}`, {
    method: 'DELETE',
    body: JSON.stringify({ message }),
  });
}

export async function cancelAutomaticRemoveGroup(
  groupId: string,
  options?: ApiClientOptions
): Promise<Response> {
  const apiClient = await buildApiClientFromNextJsContext(options?.context);
  return apiClient(`/api/groups/${groupId}/cancel-automatic-remove`, {
    method: 'PUT',
  });
}
